import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import "../style.scss";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { getLogHourList } from "../../../core/data/redux/logHour/_logHourAction";
import { logHourFormatTime } from "../../../constants/date";
import SearchIcon from "../../../icons/SearchIcon";
import { mainCategoryList } from "../../../services/category.service";
import {
  DeleteModalType,
  logHourStatusOptions,
} from "../../../constants/utlis";
import { getPropertyListForFilter } from "../../../core/data/redux/property/_propertyAction";
import ArrowUpWhiteIcon from "../../../icons/ArrowUpWhiteIcon";
import UploadWhiteIcon from "../../../icons/UploadWhiteIcon";
import DownloadIcon from "../../../icons/DownloadIcon";
import PlusIcon from "../../../icons/PlusIcon";
import ThreeDotsIcon from "../../../icons/ThreeDotsIcon";
import EditGreyIcon from "../../../icons/EditGreyIcon";
import TagBlueIcon from "../../../icons/TagBlueIcon";
import RejectWhiteIcon from "../../../icons/RejectWhiteIcon";
import LocationIcon from "../../../icons/LocationIcon";
import ArchiveIcon from "../../../icons/ArchiveIcon";
import DeleteModal from "../../../components/deleteModal";
import BuildingIcon from "../../../icons/BuildingIcon";
import UploadCsvModal from "../../../core/components/modals/uploadCsvModal";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { apiPageLimit, capitalizeFullName } from "../../../constants/common";
import http from "../../../services/http.service";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

const getExportErrorMessage = async (
  data: Blob | string | Record<string, unknown>
): Promise<string | null> => {
  try {
    let parsed: Record<string, unknown>;

    if (data instanceof Blob) {
      parsed = JSON.parse(await data.text());
    } else if (typeof data === "string") {
      parsed = JSON.parse(data);
    } else {
      parsed = data;
    }

    if (parsed.success === false && typeof parsed.error === "string") {
      return parsed.error;
    }
  } catch {
    // Response is not a JSON error payload
  }

  return null;
};

const LogHours: React.FC = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const logHourData = useSelector((state: any) => state?.logHour);
  const logHours = logHourData?.logHourList;
  const totalCount = logHourData?.totalCount;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;

  const [mainCategories, setMainCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>("");
  const [statusOptions] = useState(logHourStatusOptions);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  const [modalType, setModalType] = useState<DeleteModalType>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState<string | null>(null);

  const [showUploadCsvModal, setShowUploadCsvModal] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter properties by searchQuery
  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch log hours on component mount
  useEffect(() => {
    dispatch(getLogHourList({}));
    getMainCategoryList();
    getAllPropertyList();
  }, [dispatch]);

  // get main category list
  const getMainCategoryList = async () => {
    try {
      const response = await mainCategoryList();
      const data = response?.data?.data || [];

      // Convert API response into {label, value}
      const mapped = data.map((cat: any) => ({
        label: cat.name,
        value: cat.uuid,
      }));

      setMainCategories([{ label: "Choose Activity", value: "" }, ...mapped]);
      return;
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({}));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch properties with debounce
  const fetchProperties = useCallback(
    debounce(
      (
        search: string,
        selectedMainCategory: string,
        page: number,
        selectedStatus: string,
        selectedProperty: string
      ) => {
        const params: any = { page, limit: pageSize };
        if (search) params.search = search;
        if (selectedMainCategory) params.main_category = selectedMainCategory;
        if (selectedStatus) params.status = selectedStatus;
        if (selectedProperty) params.property = selectedProperty;
        dispatch(getLogHourList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchProperties(
      searchTerm,
      selectedMainCategory,
      page,
      selectedStatus,
      selectedProperty
    );
  }, [
    searchTerm,
    selectedMainCategory,
    page,
    selectedStatus,
    selectedProperty,
    fetchProperties,
  ]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenDeleteModal = (type: DeleteModalType, uuid: string) => {
    setModalType(type);
    setSelectedUserUuid(uuid);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUserUuid(null);
  };

  const handleOpenUploadModal = () => {
    setShowUploadCsvModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadCsvModal(false);
  };


  const handleDownloadCsv = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDownloadingCsv) return;

    setIsDownloadingCsv(true);

    try {
      const response = await http.get("/log-hours/download/csv", {
        responseType: "blob", // important for proper download
      });

      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const errorMessage = await getExportErrorMessage(response.data);
        if (errorMessage) {
          toast.error(errorMessage);
          return;
        }
      }

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `log-hours-${new Date().toISOString().slice(0, 10)}.csv`;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const blobData = error?.response?.data;
      if (blobData instanceof Blob) {
        const errorMessage = await getExportErrorMessage(blobData);
        if (errorMessage) {
          toast.error(errorMessage);
          return;
        }
      }

      console.error("Failed to download CSV:", error);
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleDownloadCsvXLSX = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDownloadingCsv) return;

    setIsDownloadingCsv(true);

    try {
      // Get CSV as text
      const response = await http.get("/log-hours/download/csv");

      const errorMessage = await getExportErrorMessage(response.data);
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }

      const csvText = response.data;

      // Convert CSV text → worksheet
      const worksheet = XLSX.utils.sheet_to_json(
        XLSX.read(csvText, { type: "string" }).Sheets.Sheet1
      );

      // Create new workbook
      const newWorkbook = XLSX.utils.book_new();

      // Convert JSON back to worksheet (clean table format)
      const newWorksheet = XLSX.utils.json_to_sheet(worksheet);

      XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, "Log Hours");

      // Generate Excel file
      const excelBuffer = XLSX.write(newWorkbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `log-hours-${new Date().toISOString().slice(0, 10)}.xlsx`;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error: any) {
      const blobData = error?.response?.data;
      if (blobData instanceof Blob) {
        const errorMessage = await getExportErrorMessage(blobData);
        if (errorMessage) {
          toast.error(errorMessage);
          return;
        }
      }

      console.error("Failed to download Excel:", error);
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleClearAllFilters = () => {
    setSelectedMainCategory("");
    setSelectedStatus("");
    setSelectedProperty("");
    setSelectedPropertyName("");
    setSearchTerm("");
  }

  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings log-hour-list-page-section">
          <div className="rounded log-hour-list-page">
            <div className="add-properties-heading d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div className="heading-text-area">
                <h2 className="mb-0">Log Hours</h2>
              </div>

              <div className="heading-button-area d-flex gap-3 flex-wrap">
                <button
                  type="button"
                  className="requests btn outlineButton  btn-outline d-inline-flex justify-content-center align-items-center  btn-block"
                  onClick={() => navigate(routes.logHoursRequests)}
                >
                  Approvals
                  <ArrowUpWhiteIcon />
                </button>


                {/* <div className="d-flex flex-column align-items-start"> */}
                <button
                  type="button"
                  className="upload-cvs btn primaryButton btn-primary d-inline-flex justify-content-center align-items-center"
                  onClick={handleOpenUploadModal}
                >
                  <UploadWhiteIcon />
                  Upload CSV
                </button>
                {/* </div> */}

                <button
                  type="button"
                  className=" add-log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                  onClick={() => navigate(routes.addLogHours)}
                >
                  <PlusIcon />
                  Log Hours
                </button>
              </div>

              <div className="w-100 mt-2 text-end">
                <a
                  href="#"
                  className="text-primary text-decoration-underline d-inline-flex align-items-center gap-2 small"
                  onClick={handleDownloadCsv}
                >
                  {isDownloadingCsv ? "Downloading…" : "Sample CSV"}
                  <DownloadIcon />
                </a>
              </div>
            </div>

            <div className="g-3 mb-3 row-boder-line p-3">
              <div className="row">
                {/* Search log hours */}
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-5 mb-2">
                  <div className="input-group commonInput bg-light bg-white">
                    <span className="input-group-text bg-white border-end-0 ">
                      <SearchIcon />
                    </span>

                    <input
                      type="text"
                      placeholder="Search log hours"
                      className="name form-control border-start-0 "
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Category select */}
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-2 mb-2">
                  <select
                    className="form-select form-control commonInput"
                    value={selectedMainCategory}
                    onChange={(e) => setSelectedMainCategory(e.target.value)}
                  >
                    <option value="" disabled hidden>
                      Choose Activity
                    </option>

                    {mainCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Property dropdown */}
                <div
                  className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-2 mb-2"
                  ref={dropdownRef}
                >
                  <div className="dropdown log-hour-list-page-dropdown">
                    <button
                      className="form-select d-flex justify-content-between align-items-center form-control commonInput"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      {selectedPropertyName || "Select Property"}
                      <i
                        className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div
                        className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                        style={{ maxHeight: "initial" }}
                      >
                        <div className="input-group mb-3 commonInput bg-light bg-white">
                          <span className="input-group-text bg-white border-end-0 ">
                            <SearchIcon />
                          </span>
                          <input
                            type="text"
                            placeholder="Search properties by name or address"
                            className="name form-control border-start-0"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>

                        {filteredProperties?.length > 0 ? (
                          filteredProperties.map((property, index) => (
                            <div
                              key={property?.uuid || index}
                              className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput bg-light"
                              onClick={() => {
                                setSelectedProperty(property?.uuid);
                                setSelectedPropertyName(property?.name);
                                setIsDropdownOpen(false);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <label className="form-check-label ms-2">
                                <div className="label-inner-content d-flex flex-column gap-2">
                                  <strong>{property?.name}</strong>
                                  <small className="text-muted d-flex align-items-center">
                                    <LocationIcon />
                                    {property?.address}, {property?.city},{" "}
                                    {property?.state} {property?.zipcode}
                                  </small>
                                </div>
                              </label>
                              <input
                                type="radio"
                                className="form-check-input mt-1"
                                name="propertyRadio"
                                checked={
                                  selectedPropertyName === property?.name
                                }
                                readOnly
                              />
                            </div>
                          ))
                        ) : (
                          <div className="text-muted text-center">
                            No results found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status select */}
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-2 mb-2">
                  <select
                    className="form-select form-control commonInput"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="" disabled hidden>
                      Select Status
                    </option>

                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-1 mb-2">
                  <div className="clear-all-button">
                    <button onClick={() => handleClearAllFilters()}>Clear All</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="row align-items-stretch log-hours-grid-wrapper">
              {logHours?.length > 0 ? (
                logHours?.map((logHour: any) => {
                  return (
                    <div className="mb-4 col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-3">
                      <div
                        className="custom-card mx-auto  h-100"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/log-hours/detail/${logHour?.uuid}`)
                        }
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start flex-wrap">
                            <span
                              className={
                                logHour?.main_category?.name.toLowerCase() ==
                                  "material participation"
                                  ? "category-badge"
                                  : "category-badge yellow"
                              }
                            >
                              {logHour?.main_category?.name}
                            </span>

                            {logHour?.user?.role?.toLowerCase() === "owner" && (
                              <div className="dropdown">
                                <button
                                  className="three-dot-btn rotate90"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <ThreeDotsIcon />
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                  <li
                                    onClick={(event) => {
                                      event.stopPropagation(); // Prevent click from bubbling to parent
                                      navigate(
                                        `/log-hours/add/${logHour?.uuid}`
                                      );
                                    }}
                                  >
                                    <a className="dropdown-item">
                                      <EditGreyIcon />
                                      Edit
                                    </a>
                                  </li>
                                  <li
                                    onClick={(event) => {
                                      event.stopPropagation(); // Prevent click from bubbling to parent
                                      handleOpenDeleteModal(
                                        "archiveLogHour",
                                        logHour?.uuid
                                      );
                                    }}
                                  >
                                    <a className="dropdown-item">
                                      <ArchiveIcon />
                                      Archive
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>

                          <div className="text-area d-flex justify-content-between align-items-center flex-wrap">
                            <h5 className="mt-3">
                              {logHour?.sub_activity?.name}
                            </h5>

                            <div className="d-flex justify-content-end mt-2">
                              <span className="time-badge">
                                {logHourFormatTime({
                                  hours: logHour?.hours,
                                  minutes: logHour?.minutes,
                                })}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 d-flex gap-2 justify-content-between align-items-center flex-wrap">
                            <span className="tag tag-red">
                              <BuildingIcon /> &nbsp;
                              {logHour?.property?.name}
                            </span>

                            <span className="tag tag-blue">
                              <TagBlueIcon /> &nbsp;
                              {logHour?.activity?.name}
                            </span>
                          </div>
                        </div>

                        <div className="status-row d-flex justify-content-between align-items-center">
                          <span className="status-badge">Logged By</span>
                          <div className="d-flex align-items-center logged-by rejected-area">
                            <img
                              className="img-fluid"
                              src={
                                logHour?.user?.image ? `${logHour?.user?.image}` : "/assets/img/userCircle.png"
                              }
                              alt="peofile"
                            />
                            <div>
                              <span className="owner-role">
                                {logHour?.user?.role_label}
                              </span>
                              <span className="fw-semibold d-block text-dark client-name">
                                {capitalizeFullName(logHour?.user?.name)}
                              </span>
                            </div>
                          </div>

                          {logHour?.cpa_comment && (
                            <span className="status-badge-2 commented">
                              Commented
                            </span>
                          )}

                          {logHour?.status?.toLowerCase() == "reject" && (
                            <>
                              <span className="status-badge-2 rejected">
                                Rejected
                              </span>

                              {logHour?.reason && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip
                                      id="alert-tooltip"
                                      className="white-tooltip"
                                    >
                                      {logHour?.reason}
                                    </Tooltip>
                                  }
                                >
                                  <div className="alert-icon" role="button">
                                    <RejectWhiteIcon />
                                  </div>
                                </OverlayTrigger>
                              )}
                            </>
                          )}

                          {(logHour?.status?.toLowerCase() == "accept") && (logHour?.user?.role?.toLowerCase() != "owner") && (
                            <span className="status-badge-2 accepted">
                              Accepted
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center w-100 py-4">No data found.</p>
              )}
            </div>

            {/* Pagination */}
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </main>

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type={modalType}
        uuid={selectedUserUuid}
      />

      <UploadCsvModal
        show={showUploadCsvModal}
        onHide={handleCloseUploadModal}
        type="uploadLogHourCsv"
      />
    </div>
  );
};

export default LogHours;
